/**
 * Design System - Skeleton Component
 *
 * Skeleton loaders provide visual placeholders during content loading,
 * improving perceived performance and user experience.
 *
 * Features:
 * - Smooth CSS animations
 * - Multiple shape variants (text, circle, rectangle)
 * - Customizable animation types (pulse, shimmer, wave)
 * - Responsive sizing
 * - Composable for complex layouts
 * - Accessibility support
 *
 * @example
 * // Simple text line
 * <Skeleton variant="text" />
 *
 * @example
 * // Avatar circle with shimmer
 * <Skeleton variant="circle" width="48px" height="48px" animation="shimmer" />
 *
 * @example
 * // Custom rectangle with wave animation
 * <Skeleton variant="rectangle" width="100%" height="200px" animation="wave" />
 *
 * @example
 * // Multiple lines with stagger effect
 * <Skeleton variant="text" count={3} animation="pulse" />
 */

import React from 'react';
import PropTypes from 'prop-types';
import { cn } from '../utils/classNames';

const Skeleton = ({
  variant = 'text',
  width,
  height,
  className,
  count = 1,
  spacing = 'sm',
  animation = 'shimmer',
  duration = 1.5,
}) => {
  // Spacing between skeleton lines
  const spacingStyles = {
    none: 'space-y-0',
    xs: 'space-y-1',
    sm: 'space-y-2',
    md: 'space-y-3',
    lg: 'space-y-4',
  };

  // Animation class names
  const animationStyles = {
    pulse: 'animate-pulse',
    shimmer: 'skeleton-shimmer',
    wave: 'skeleton-wave',
    fade: 'animate-pulse',
  };

  // Base styles
  const baseStyles = cn(
    'bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 relative overflow-hidden',
    animationStyles[animation],
    className
  );

  // Variant-specific styles
  const variantStyles = {
    text: 'h-4 rounded',
    circle: 'rounded-full',
    rectangle: 'rounded-lg',
    button: 'h-10 rounded-md',
    avatar: 'rounded-full',
    card: 'rounded-lg',
  };

  // Default dimensions for variants
  const defaultDimensions = {
    text: { width: '100%', height: undefined },
    circle: { width: '40px', height: '40px' },
    rectangle: { width: '100%', height: '100px' },
    button: { width: '120px', height: '40px' },
    avatar: { width: '48px', height: '48px' },
    card: { width: '100%', height: '200px' },
  };

  const dimensions = {
    width: width || defaultDimensions[variant].width,
    height: height || defaultDimensions[variant].height,
  };

  const skeletonStyle = {
    width: dimensions.width,
    height: dimensions.height,
    animationDuration: `${duration}s`,
  };

  // Render single skeleton
  const renderSkeleton = (index) => (
    <div
      key={index}
      className={cn(baseStyles, variantStyles[variant])}
      style={{
        ...skeletonStyle,
        ...(animation === 'shimmer' && {
          backgroundSize: '200% 100%',
        }),
        animationDelay: count > 1 ? `${index * 0.1}s` : '0s',
      }}
      role="status"
      aria-label="Cargando..."
      aria-live="polite"
    >
      {/* Wave overlay effect */}
      {animation === 'wave' && (
        <div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent skeleton-wave-overlay"
          style={{ animationDuration: `${duration * 1.5}s` }}
        />
      )}
      <span className="sr-only">Cargando...</span>
    </div>
  );

  // Render multiple skeletons
  if (count > 1) {
    return (
      <div className={cn(spacingStyles[spacing])}>
        {Array.from({ length: count }, (_, index) => renderSkeleton(index))}
      </div>
    );
  }

  return renderSkeleton(0);
};

Skeleton.propTypes = {
  /** Visual variant of the skeleton */
  variant: PropTypes.oneOf(['text', 'circle', 'rectangle', 'button', 'avatar', 'card']),
  /** Width (CSS value: px, %, rem, etc.) */
  width: PropTypes.string,
  /** Height (CSS value: px, %, rem, etc.) */
  height: PropTypes.string,
  /** Additional CSS classes */
  className: PropTypes.string,
  /** Number of skeleton lines to render */
  count: PropTypes.number,
  /** Spacing between multiple skeletons */
  spacing: PropTypes.oneOf(['none', 'xs', 'sm', 'md', 'lg']),
  /** Animation type (pulse, shimmer, wave, fade) */
  animation: PropTypes.oneOf(['pulse', 'shimmer', 'wave', 'fade']),
  /** Animation duration in seconds */
  duration: PropTypes.number,
};

export default Skeleton;
